import { Component, inject, OnInit } from "@angular/core";
import { TreeNode } from "primeng/api";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { FileInfo } from "src/app/common/components/file-content-list/file-content-list.component";
import { ConfigService } from "src/app/common/services/config.service";
import { LlmConfigItem, LlmConfigService } from "src/app/common/services/llm-config.service";
import { NamedPromptCategory } from "src/app/common/services/named-prompt-category.service";
import { PredefinedPrompt } from "src/app/modules/prompts/models/prompt-template.model";
import { PromptsService } from "src/app/modules/prompts/services/prompts.service";

@Component({
  templateUrl: "./prompt-editor.component.html",
  styleUrls: ["./prompt-editor.component.scss"],
})
export class PromptEditorComponent implements OnInit {
  protected readonly ref = inject(DynamicDialogRef);
  protected readonly configService = inject(ConfigService);
  protected readonly promptsService = inject(PromptsService);
  protected readonly llmConfigService = inject(LlmConfigService);

  temperature!: number;

  promptLabel = $localize`:@@prompt_editor_new_prompt_default:Nouveau prompt`;
  model!: string;
  context!: string;
  prompt!: string;
  llmDescriptions = Object.entries(this.llmConfigService.llmConfigs)
    .map(([key, llm]: [string, LlmConfigItem]) => `<LI><B>${llm.displayedName}</B>: ${llm.description}`)
    .join("\n");
  promptCategory!: NamedPromptCategory;
  action!: "add" | "edit" | "copy";
  optionsModel: object[] = [];
  protected readonly dialogConfig: DynamicDialogConfig<{
    action: "add" | "edit" | "copy";
    promptCategory: NamedPromptCategory;
    currentPrompt?: PredefinedPrompt;
    id: string;
    parent: TreeNode<PredefinedPrompt>;
  }> = inject(DynamicDialogConfig);

  contextFiles: FileInfo[] = [];

  // Whether “Ancrage des recherches” is checked
  searchGrounding = false;

  // Whether the currently-selected model is allowed to search
  searchEnabled = false;

  get dynamicButtonLabel(): string {
    return this.action === "add"
      ? $localize`:@@prompt_editor_add_button_label:Ajouter`
      : $localize`:@@prompt_editor_save_button_label:Sauvegarder`;
  }

  async updateSearchEnabled(): Promise<void> {
    this.searchEnabled = await this.llmConfigService.isModelSearchEnabled(this.model);
    if (!this.searchEnabled) {
      this.searchGrounding = false;
    }
  }

  get selectedModelDisplayName(): string {
    const item = Object.values(this.llmConfigService.llmConfigs).find((m) => m.technicalName === this.model);
    return item ? item.displayedName : this.model;
  }

  async ngOnInit(): Promise<void> {
    this.action = this.dialogConfig.data!.action;
    this.promptCategory = this.dialogConfig.data!.promptCategory;
    const currentPrompt = this.dialogConfig.data!.currentPrompt;
    this.model = currentPrompt?.model ?? this.configService.defaultLlm;
    this.temperature = currentPrompt?.temperature ?? 0.5;
    this.context = currentPrompt?.systemPrompt ?? "";
    this.prompt = currentPrompt?.userPrompt ?? "";
    this.contextFiles = currentPrompt?.contextFiles ?? [];
    this.searchGrounding = Boolean(currentPrompt?.searchGrounding);
    if (this.action === "edit") {
      this.promptLabel = currentPrompt!.label;
    } else if (this.action === "copy" && currentPrompt?.label) {
      this.promptLabel = $localize`:@@prompt_editor_copied_prompt_label:${currentPrompt.label} (copie)`;
    } else {
      this.promptLabel = $localize`:@@prompt_editor_new_prompt_label:Nouveau prompt`;
    }

    this.optionsModel = await this.llmConfigService.getLlmOptions();
    await this.updateSearchEnabled();
  }

  onModelChange(newValue: string): void {
    this.model = newValue;
    // Update whether search is allowed with the selected model
    void this.updateSearchEnabled().then(() => {
      // Always reset checkbox state when switching models, even if both support search
      this.searchGrounding = false;
    });
  }
  async savePrompt(): Promise<void> {
    const id = this.action === "edit" ? this.dialogConfig.data!.currentPrompt!.id : crypto.randomUUID();

    let parentId: string;

    if (this.action === "edit") {
      parentId = this.dialogConfig.data!.currentPrompt!.parentId!;
    } else if (this.action === "copy") {
      parentId = this.dialogConfig.data!.parent.data!.id!;
    } else {
      parentId = this.dialogConfig.data!.currentPrompt!.id!;
    }

    if (this.promptCategory === "MES_PROMPTS" || (await this.promptsService.isUserPrompt(parentId!))) {
      await this.promptsService.saveUserPrompt(parentId!, {
        id,
        title: this.promptLabel,
        prompt: this.prompt,
        context: this.context,
        temperature: this.temperature,
        model: this.model,
        contextFiles: this.contextFiles,
        searchGrounding: this.searchGrounding,
        parentId,
        order: 0,
      });
    } else {
      await this.promptsService.saveTemplatePrompt(parentId!, {
        id,
        label: this.promptLabel,
        userPrompt: this.prompt,
        systemPrompt: this.context,
        temperature: this.temperature,
        model: this.model,
        contextFiles: this.contextFiles,
        searchGrounding: this.searchGrounding,
        parentId,
        order: 0,
      });
    }
    this.ref.close({
      node: {
        label: this.promptLabel,
        data: {
          id,
          label: this.promptLabel,
          userPrompt: this.prompt,
          systemPrompt: this.context,
          temperature: this.temperature,
          contextFiles: this.contextFiles,
          searchGrounding: this.searchGrounding,
          model: this.model,
        },
      },
    });
  }

  onFileSelected(files: FileInfo[]): void {
    const selectedFileIds = new Set(files.map((file) => file.id));
    this.contextFiles = this.contextFiles.filter((file) => !selectedFileIds.has(file.id));
    this.contextFiles = this.contextFiles.concat(files);
  }

  removeContextFile(id: string): void {
    this.contextFiles = this.contextFiles.filter((file) => file.id !== id);
  }
}
